/**
 * ч.1098: «Аудит проводок».
 * Сервіс перевіряє проводки організації за період набором правил (без моделі
 * і без кредитів) і показує місця, схожі на помилки. Бухгалтер вирішує по
 * кожній знахідці: «перевірено» (усе правильно), «ігнорувати» або виправляє
 * документ — тоді наступна перевірка сама позначить знахідку «виправлено».
 * «пояснити (Oblik+/AI)» — пояснення агента; окреме право, бо витрачає кредити.
 * В обліку сторінка нічого не змінює.
 */
import { useMemo, useState, useSyncExternalStore } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import DatePopover from '@/components/DatePopover';
import { useToast } from '@/components/ToastProvider';
import { ai as aiApi } from '@/api/ai';
import { aiAvailableText } from '@/shared/utils/aiCredits';
import {
  postingAudit, type AuditFinding, type AuditPeriod, type AuditRuleSummary,
  type AuditSeverity, type AuditVerdict, type FindingStatus, type ManualStatus,
} from '@/api/postingAudit';
import { useAllPermissionsByOrg, usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import AiBadge from '@/shared/components/AiBadge';
import PostingAuditScanPanel from '@/pages/PostingAuditScanPanel';
import { DOC_ROUTES } from '@/shared/utils/docRoutes';
import uiPrefs from '@/store/uiPrefs';
import { ledgerLinks } from '@/pages/ledger/ledgerModel';
import { extractApiError } from '@/utils/extractApiError';

const SEVERITY: Record<AuditSeverity, { label: string; token: string }> = {
  ERROR: { label: 'помилка', token: 'red' },
  WARN: { label: 'увага', token: 'amber' },
  INFO: { label: 'до відома', token: 'blue' },
};

export const STATUS_LABEL: Record<FindingStatus, string> = {
  OPEN: 'відкрито',
  CHECKED: 'перевірено',
  IGNORED: 'ігнорується',
  RESOLVED: 'виправлено',
};

const VERDICT: Record<AuditVerdict, { label: string; token: string }> = {
  likely_error: { label: 'схоже на помилку', token: 'red' },
  likely_ok: { label: 'схоже, усе правильно', token: 'green' },
  unclear: { label: 'потрібна перевірка', token: 'amber' },
};

type StatusFilter = FindingStatus | 'ALL';

const DOC_KIND: Record<string, string> = {
  'doc.goods_receipts': 'Прибуткова накладна',
  'doc.goods_sales': 'Реалізація',
  'doc.bank_payments': 'Банк, списання',
  'doc.bank_receipts': 'Банк, надходження',
  'doc.cash_payments': 'Видатковий касовий ордер',
  'doc.cash_receipts': 'Прибутковий касовий ордер',
  'doc.manual_operations': 'Ручна операція',
  'doc.payrolls': 'Нарахування зарплати',
  'doc.goods_writeoffs': 'Списання товарів',
  'doc.production_reports': 'Звіт виробництва',
  'doc.material_requisitions': 'Вимога-накладна',
  'doc.advance_reports': 'Авансовий звіт',
};

function badge(token: string): React.CSSProperties {
  if (token === 'gray') {
    return {
      color: 'var(--muted)', background: 'var(--bg-soft)', border: '1px solid var(--border)',
      borderRadius: 4, padding: '0 0.4rem', fontSize: '0.75rem', fontWeight: 600, whiteSpace: 'nowrap',
    };
  }
  return {
    color: `var(--badge-${token}-fg)`, background: `var(--badge-${token}-bg)`,
    border: `1px solid var(--badge-${token}-bd)`, borderRadius: 4, padding: '0 0.4rem',
    fontSize: '0.75rem', fontWeight: 600, whiteSpace: 'nowrap',
  };
}

const fmtMoney = (v: string | null) => (v == null ? '' : Number(v).toLocaleString('uk-UA', {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
}));
const fmtDate = (v: string | null) => (v ? v.slice(0, 10).split('-').reverse().join('.') : '');
const fmtCredits = (v: string) => Number(v).toFixed(2);

function countFor(rule: AuditRuleSummary, filter: StatusFilter): number {
  if (filter === 'ALL') return rule.open + rule.checked + rule.ignored + rule.resolved;
  return { OPEN: rule.open, CHECKED: rule.checked, IGNORED: rule.ignored, RESOLVED: rule.resolved }[filter];
}

/** Ключ налаштування «які області перевіряти» (памʼятається для користувача). */
export const AREAS_PREF_KEY = 'posting-audit.areas';

const str = (v: unknown): string => (typeof v === 'string' ? v : '');

/**
 * Посилання для знахідки без документа (ч.1100): картка рахунку за потрібний
 * період або картка товару. null — посилатись нікуди.
 */
export function findingLink(f: AuditFinding, period: AuditPeriod): { to: string; label: string } | null {
  const d = f.details;
  const org = f.organization_id;
  if (f.rule_code === 'AUD-006' && str(d.acc)) {
    return { label: `картка рахунку ${str(d.acc)}`, to: ledgerLinks.accountCard({
      org, account: str(d.acc), from: str(d.link_from) || period.date_from, to: str(d.link_to) || period.date_to }) };
  }
  if (f.rule_code === 'AUD-010' && str(d.acc)) {
    return { label: `картка рахунку ${str(d.acc)}`, to: ledgerLinks.accountCard({
      org, account: str(d.acc), from: period.date_from, to: period.date_to }) };
  }
  if (f.rule_code === 'AUD-009' && str(d.contractor_id)) {
    return { label: 'рух по контрагенту', to: ledgerLinks.accountCard({
      org, account: null, sc: str(d.contractor_id), scName: str(d.contractor),
      from: period.date_from, to: period.date_to }) };
  }
  if (f.rule_code === 'AUD-011' && str(d.nomenclature_id)) {
    return { label: 'картка товару', to: `/nomenclature/${str(d.nomenclature_id)}` };
  }
  return null;
}

/** Бейдж «імпортовано з 1С» (Закон дизайну #3). */
function ImportedMark() {
  return (
    <span title="Документ перенесено з 1С: проводки скопійовано без змін" style={{
      color: 'var(--badge-blue-fg)', border: '1px solid var(--badge-blue-bd)', borderRadius: 4,
      fontSize: '0.7rem', fontWeight: 700, padding: '0 4px', marginRight: 4,
    }}>I</span>
  );
}

function AiBlock({ f }: { f: AuditFinding }) {
  if (!f.ai) return null;
  const v = VERDICT[f.ai.verdict];
  return (
    <div data-testid="ai-explanation" style={{
      marginTop: '0.35rem', padding: '0.4rem 0.6rem', borderRadius: 6,
      background: 'var(--bg-soft)', border: '1px solid var(--border)', whiteSpace: 'normal',
    }}>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={badge(v.token)}>{v.label}</span>
        <span style={{ color: 'var(--muted)', fontSize: '0.75rem' }}>
          Oblik+/AI · {fmtCredits(f.ai.credits)} кредитів
        </span>
      </div>
      <div style={{ marginTop: '0.25rem' }}>{f.ai.explanation}</div>
      <div style={{ marginTop: '0.25rem' }}><b>Що зробити: </b>{f.ai.suggestion}</div>
      {f.ai.run.escalated && (
        <div style={{ color: 'var(--muted)', fontSize: '0.75rem', marginTop: '0.25rem' }}>
          Відповідь уточнено точнішим аналізом; списано за обидві спроби:{' '}
          {f.ai.run.attempts.map((a) => fmtCredits(a.credits)).join(' + ')} кредитів.
        </div>
      )}
      {f.ai.warning && (
        <div style={{ color: 'var(--badge-amber-fg)', fontSize: '0.75rem', marginTop: '0.25rem' }}>
          Перевірте відповідь: {f.ai.warning}.
        </div>
      )}
    </div>
  );
}

interface RuleBlockProps {
  rule: AuditRuleSummary;
  orgId: string;
  period: AuditPeriod;
  filter: StatusFilter;
  canManage: boolean;
  canAi: boolean;
  aiAvailable: string | null;
  truncated: boolean;
}

function RuleBlock({ rule, orgId, period, filter, canManage, canAi, aiAvailable, truncated }: RuleBlockProps) {
  const qc = useQueryClient();
  const toast = useToast();
  const [open, setOpen] = useState(rule.severity === 'ERROR');
  const [busyAi, setBusyAi] = useState<string | null>(null);
  const count = countFor(rule, filter);
  const key = ['posting-audit', 'findings', orgId, period.date_from, period.date_to, rule.code, filter];
  const q = useQuery({
    queryKey: key,
    queryFn: () => postingAudit.findings(orgId, period, rule.code, filter === 'ALL' ? undefined : filter),
    enabled: open && count > 0,
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ['posting-audit'] });
  const onError = (e: unknown) => toast.show({ type: 'error', message: extractApiError(e) });

  const statusMut = useMutation({
    mutationFn: (v: { id: string; status: ManualStatus }) => postingAudit.setStatus(v.id, v.status),
    onSuccess: refresh, onError,
  });
  const noteMut = useMutation({
    mutationFn: (v: { id: string; note: string }) => postingAudit.setNote(v.id, v.note),
    onSuccess: refresh, onError,
  });
  const bulkMut = useMutation({
    mutationFn: (status: ManualStatus) => postingAudit.bulkStatus(orgId, period, rule.code, status, 'OPEN'),
    onSuccess: refresh, onError,
  });
  const explainMut = useMutation({
    mutationFn: (id: string) => postingAudit.explain(id),
    onMutate: (id) => setBusyAi(id),
    onSettled: () => setBusyAi(null),
    onSuccess: () => { void refresh(); void qc.invalidateQueries({ queryKey: ['ai-balance'] }); },
    onError,
  });

  const noManage = 'Немає права приймати рішення по знахідках у цій організації';
  const cols = useMemo<ColumnDef<AuditFinding>[]>(() => [
    {
      key: 'date', header: 'Дата', width: 90,
      cell: (f) => fmtDate(f.document_date ?? f.period_date),
      sortValue: (f) => (f.document_date ?? f.period_date ?? ''),
    },
    {
      key: 'doc', header: 'Документ', width: 220,
      cell: (f) => {
        if (!f.document_type) {
          const l = findingLink(f, period);
          return l ? <Link to={l.to} target="_blank">{l.label}</Link> : '—';
        }
        const label = `${DOC_KIND[f.document_type] ?? 'Документ'} № ${f.document_number ?? '—'}`;
        const to = f.document_id ? DOC_ROUTES[f.document_type]?.(f.document_id) : undefined;
        return (
          <>
            {f.details.imported === true && <ImportedMark />}
            {to ? <Link to={to} target="_blank">{label}</Link> : label}
          </>
        );
      },
      sortValue: (f) => f.document_number ?? '',
    },
    {
      key: 'amount', header: 'Сума', align: 'right', width: 120,
      cell: (f) => fmtMoney(f.amount), sortValue: (f) => Number(f.amount ?? 0),
    },
    {
      key: 'message', header: 'Що знайдено',
      cell: (f) => (
        <div style={{ whiteSpace: 'normal' }}>
          {rule.code === 'AI-001' && (
            <span style={{ ...badge(SEVERITY[f.severity].token), marginRight: '0.4rem' }}>
              {SEVERITY[f.severity].label}
            </span>
          )}
          {f.message}
          {Array.isArray(f.details.reasons) && (
            <div style={{ color: 'var(--muted)', fontSize: '0.8rem' }}>
              Чому агент дивився цей документ: {(f.details.reasons as string[]).join(', ')}.
            </div>
          )}
          {f.details.imported === true && (
            <div style={{ color: 'var(--muted)', fontSize: '0.8rem' }}>
              Документ перенесено з 1С — його проводки скопійовано без змін; виправлення робиться
              бухгалтерською довідкою (ручною операцією).
            </div>
          )}
          {f.note && <div style={{ color: 'var(--muted)', fontSize: '0.8rem' }}>Примітка: {f.note}</div>}
          <AiBlock f={f} />
        </div>
      ),
      sortValue: (f) => f.message,
    },
    {
      key: 'status', header: 'Стан', width: 110,
      cell: (f) => (
        <span title={f.status_by_name ? `${f.status_by_name}, ${fmtDate(f.status_at)}` : undefined}
          style={badge({ OPEN: 'amber', CHECKED: 'green', IGNORED: 'gray', RESOLVED: 'green' }[f.status])}>
          {STATUS_LABEL[f.status]}
        </span>
      ),
      sortValue: (f) => f.status,
    },
    {
      key: 'actions', header: '', width: 380,
      cell: (f) => {
        const resolved = f.status === 'RESOLVED';
        const lock = !canManage ? noManage
          : resolved ? 'Знахідку вже виправлено — стан змінить наступна перевірка' : undefined;
        return (
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {f.status === 'OPEN' ? (
              <>
                <button type="button" className="btn-link" disabled={!!lock || statusMut.isPending}
                  title={lock ?? 'Перевірив: операція правильна'}
                  onClick={() => statusMut.mutate({ id: f.id, status: 'CHECKED' })}>перевірено</button>
                <button type="button" className="btn-link" disabled={!!lock || statusMut.isPending}
                  title={lock ?? 'Не показувати цю знахідку серед відкритих'}
                  onClick={() => statusMut.mutate({ id: f.id, status: 'IGNORED' })}>ігнорувати</button>
              </>
            ) : (
              <button type="button" className="btn-link" disabled={!!lock || statusMut.isPending}
                title={lock ?? 'Повернути знахідку у відкриті'}
                onClick={() => statusMut.mutate({ id: f.id, status: 'OPEN' })}>повернути</button>
            )}
            <button type="button" className="btn-link" disabled={!canManage || noteMut.isPending}
              title={canManage ? 'Записати пояснення до свого рішення — його побачать колеги' : noManage}
              onClick={() => {
                const text = window.prompt('Примітка до знахідки (порожньо — прибрати)', f.note ?? '');
                if (text !== null) noteMut.mutate({ id: f.id, note: text });
              }}>примітка</button>
            <button type="button" className="btn-link" disabled={!canAi || busyAi !== null}
              title={canAi
                ? `Oblik+/AI пояснить знахідку за даними документа. Доступно кредитів: ${aiAvailable ?? '…'}`
                : 'Немає права «Oblik+/AI: пояснення знахідок аудиту проводок» у цій організації'}
              onClick={() => explainMut.mutate(f.id)}>
              {busyAi === f.id ? 'пояснюємо…' : f.ai ? 'пояснити ще раз (Oblik+/AI)' : 'пояснити (Oblik+/AI)'}
            </button>
          </div>
        );
      },
    },
  ], [canManage, canAi, aiAvailable, busyAi, statusMut.isPending, noteMut.isPending, period, rule.code]);  // eslint-disable-line react-hooks/exhaustive-deps

  const sev = SEVERITY[rule.severity];
  return (
    <div data-testid={`audit-rule-${rule.code}`} style={{
      border: `1px solid var(--badge-${sev.token}-bd)`, borderLeft: `4px solid var(--badge-${sev.token}-bd)`,
      borderRadius: 6, padding: '0.75rem 1rem', marginBottom: '0.75rem', background: 'var(--bg-panel)',
    }}>
      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={badge(sev.token)}>{sev.label}</span>
        <strong>{rule.title}</strong>
        <span style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>{rule.area_title} · {rule.code}</span>
        <span style={{ marginLeft: 'auto', fontVariantNumeric: 'tabular-nums' }}>{count}</span>
      </div>
      <p style={{ margin: '0.5rem 0 0.25rem' }}>{rule.why}</p>
      <p style={{ margin: '0 0 0.5rem', color: 'var(--muted)' }}>
        <span style={{ fontWeight: 600 }}>Що робити: </span>{rule.action}
      </p>
      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <button type="button" className="btn-link" onClick={() => setOpen((v) => !v)}>
          {open ? 'сховати знахідки' : `показати знахідки (${count})`}
        </button>
        {rule.open > 0 && (filter === 'OPEN' || filter === 'ALL') && (
          <>
            <button type="button" className="btn-link" disabled={!canManage || bulkMut.isPending}
              title={canManage ? 'Позначити всі відкриті знахідки цієї перевірки як перевірені' : noManage}
              onClick={() => {
                if (window.confirm(`Позначити «перевірено» всі відкриті знахідки: ${rule.title} (${rule.open})?`)) {
                  bulkMut.mutate('CHECKED');
                }
              }}>перевірено всі</button>
            <button type="button" className="btn-link" disabled={!canManage || bulkMut.isPending}
              title={canManage ? 'Ігнорувати всі відкриті знахідки цієї перевірки' : noManage}
              onClick={() => {
                if (window.confirm(`Ігнорувати всі відкриті знахідки: ${rule.title} (${rule.open})?`)) {
                  bulkMut.mutate('IGNORED');
                }
              }}>ігнорувати всі</button>
          </>
        )}
      </div>
      {truncated && (
        <div style={{ color: 'var(--muted)', fontSize: '0.85rem', marginTop: '0.25rem' }}>
          Знахідок більше, ніж показується за одну перевірку (300). Розберіть ці — і перевірте ще раз,
          або звузьте період.
        </div>
      )}
      {open && count > 0 && (
        <div style={{ marginTop: '0.5rem' }}>
          <DataTable<AuditFinding>
            rows={q.data?.items ?? []} columns={cols} rowKey={(f) => f.id}
            loading={q.isLoading} emptyText="—"
            rowMuted={(f) => (f.status === 'OPEN' ? undefined : 0.65)}
          />
          {(q.data?.total ?? 0) > (q.data?.items.length ?? 0) && (
            <div style={{ color: 'var(--muted)', fontSize: '0.85rem', marginTop: '0.25rem' }}>
              показано {q.data?.items.length} з {q.data?.total}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function PostingAuditPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const { availableOrgs, hasPermissionInAnyOrg, loading: permsLoading } =
    usePermissionsByOrg('accounting.audit.view');
  const { permissionsByOrg } = useAllPermissionsByOrg();
  // Адреса може задати організацію і період (перехід із закриття місяця).
  const [sp] = useSearchParams();
  const isoParam = (k: string) => (/^\d{4}-\d{2}-\d{2}$/.test(sp.get(k) ?? '') ? sp.get(k)! : '');
  const [orgId, setOrgId] = useState(sp.get('org') ?? '');
  const [dateFrom, setDateFrom] = useState(isoParam('from') || `${new Date().getFullYear()}-01-01`);
  const [dateTo, setDateTo] = useState(isoParam('to') || todayIso());
  const [filter, setFilter] = useState<StatusFilter>('OPEN');
  const effectiveOrg = (orgId && availableOrgs.some((o) => o.id === orgId) ? orgId : '')
    || availableOrgs[0]?.id || '';
  // Області перевірки: обирає бухгалтер, вибір памʼятається (порожньо = усі).
  const areasQ = useQuery({ queryKey: ['posting-audit', 'areas'], queryFn: postingAudit.areas, staleTime: Infinity });
  const storedAreas = useSyncExternalStore(
    (cb) => uiPrefs.subscribe(AREAS_PREF_KEY, cb),
    () => uiPrefs.get<string[]>(AREAS_PREF_KEY),
    () => null,
  );
  const allAreas = useMemo(() => (areasQ.data ?? []).map((a) => a.code), [areasQ.data]);
  const areas = useMemo(
    () => (storedAreas ? allAreas.filter((a) => storedAreas.includes(a)) : allAreas),
    [storedAreas, allAreas]);
  const allSelected = areas.length === allAreas.length;
  const toggleArea = (code: string) => uiPrefs.set(AREAS_PREF_KEY,
    areas.includes(code) ? areas.filter((a) => a !== code) : allAreas.filter((a) => a === code || areas.includes(a)));
  const period = useMemo<AuditPeriod>(() => ({ date_from: dateFrom, date_to: dateTo }), [dateFrom, dateTo]);
  const periodOk = !!dateFrom && !!dateTo && dateFrom <= dateTo;
  const perms = permissionsByOrg[effectiveOrg] ?? [];
  const canManage = perms.includes('accounting.audit.manage');
  const canAi = perms.includes('ai.posting_audit.use');
  const canScan = perms.includes('ai.posting_audit_scan.use');

  const summaryQ = useQuery({
    queryKey: ['posting-audit', 'summary', effectiveOrg, dateFrom, dateTo, areas.join(',')],
    queryFn: () => postingAudit.summary(effectiveOrg, period, allSelected ? undefined : areas),
    enabled: !!effectiveOrg && periodOk && areas.length > 0,
  });
  const aiBalanceQ = useQuery({
    queryKey: ['ai-balance'], queryFn: aiApi.balance, staleTime: 60_000, enabled: canAi || canScan,
  });
  const runMut = useMutation({
    mutationFn: () => postingAudit.run(effectiveOrg, period, allSelected ? undefined : areas),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: ['posting-audit'] });
      toast.show({
        type: res.rules_failed ? 'warning' : 'success',
        message: `Перевірку виконано: знахідок ${res.found}, виправлено з минулого разу ${res.resolved}`
          + (res.rules_failed ? `; не виконано перевірок: ${res.rules_failed}` : ''),
      });
    },
    onError: (e) => toast.show({ type: 'error', message: extractApiError(e, 'Не вдалося виконати перевірку') }),
  });

  if (!permsLoading && !hasPermissionInAnyOrg) {
    return (
      <div className="card">
        <p>Немає права на аудит проводок у жодній організації.</p>
      </div>
    );
  }

  const s = summaryQ.data;
  const shown = (s?.rules ?? []).filter((r) => countFor(r, filter) > 0);
  const clean = (s?.rules ?? []).filter((r) => countFor(r, 'ALL') === 0);
  const openTotal = s ? s.open_by_severity.ERROR + s.open_by_severity.WARN + s.open_by_severity.INFO : 0;
  const tone = !s ? 'green' : s.open_by_severity.ERROR ? 'red' : s.open_by_severity.WARN ? 'amber' : 'green';

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Аудит проводок<AiBadge /></h2>
      </div>
      <p style={{ color: 'var(--muted)', margin: '0.25rem 0 0.75rem' }}>
        Сервіс переглядає проводки за період і показує місця, схожі на помилки: розрахунки без
        контрагента, нетипові рахунки, документи без проводок, дублі, відʼємні залишки грошей.
        В обліку нічого не змінюється — рішення по кожній знахідці приймаєте ви.
      </p>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <select value={effectiveOrg} onChange={(e) => setOrgId(e.target.value)} aria-label="Організація">
          {availableOrgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
        <span style={{ color: 'var(--muted)' }}>період з</span>
        <DatePopover value={dateFrom} onChange={setDateFrom} />
        <span style={{ color: 'var(--muted)' }}>по</span>
        <DatePopover value={dateTo} onChange={setDateTo} />
        <button type="button" className="primary" onClick={() => runMut.mutate()}
          disabled={!effectiveOrg || !periodOk || runMut.isPending || areas.length === 0}
          title={!periodOk ? 'Початок періоду пізніше за кінець'
            : areas.length === 0 ? 'Оберіть хоча б одну область перевірки'
              : 'Перевірити проводки організації за обраний період'}>
          {runMut.isPending ? 'перевіряємо…' : 'перевірити'}
        </button>
        <select value={filter} onChange={(e) => setFilter(e.target.value as StatusFilter)}
          aria-label="Стан знахідок" style={{ marginLeft: 'auto' }}>
          <option value="OPEN">відкриті</option>
          <option value="CHECKED">перевірені</option>
          <option value="IGNORED">проігноровані</option>
          <option value="RESOLVED">виправлені</option>
          <option value="ALL">усі</option>
        </select>
      </div>

      <div data-testid="audit-areas" style={{
        display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '1rem',
      }}>
        <span style={{ color: 'var(--muted)' }}>що перевіряти:</span>
        {(areasQ.data ?? []).map((a) => {
          const on = areas.includes(a.code);
          return (
            <button key={a.code} type="button" aria-pressed={on} onClick={() => toggleArea(a.code)}
              title={a.rules.map((r) => r.title).join('\n')}
              style={{
                padding: '0.15rem 0.6rem', borderRadius: 999, fontSize: '0.85rem',
                border: `1px solid ${on ? 'var(--primary)' : 'var(--border)'}`,
                background: on ? 'var(--primary-soft)' : 'var(--bg-panel)',
                color: on ? 'var(--primary)' : 'var(--text-secondary)',
              }}>
              {a.title} ({a.rules.length})
            </button>
          );
        })}
        {!allSelected && (
          <button type="button" className="btn-link" onClick={() => uiPrefs.set(AREAS_PREF_KEY, allAreas)}>
            обрати всі
          </button>
        )}
      </div>

      {effectiveOrg && periodOk && (
        <PostingAuditScanPanel orgId={effectiveOrg} period={period} canScan={canScan}
          aiAvailable={aiAvailableText(aiBalanceQ.data)} />
      )}

      {areas.length === 0 && areasQ.data && (
        <p style={{ color: 'var(--muted)' }}>Оберіть хоча б одну область перевірки.</p>
      )}
      {summaryQ.isLoading && <p>Завантажуємо…</p>}
      {summaryQ.isError && <p style={{ color: 'var(--badge-red-fg)' }}>Не вдалося отримати знахідки.</p>}

      {s && (
        <>
          <div data-testid="audit-summary" style={{
            padding: '0.6rem 0.9rem', borderRadius: 6, marginBottom: '1rem',
            background: `var(--badge-${tone}-bg)`, border: `1px solid var(--badge-${tone}-bd)`,
          }}>
            {s.last_run ? (
              <>
                <strong>Відкритих знахідок за період: {openTotal}</strong>
                {openTotal > 0 && (
                  <span> — помилок {s.open_by_severity.ERROR}, уваги {s.open_by_severity.WARN},
                    {' '}до відома {s.open_by_severity.INFO}</span>
                )}
                <span style={{ color: 'var(--muted)', marginLeft: '0.5rem', fontSize: '0.85rem' }}>
                  · остання перевірка {new Date(s.last_run.created_at).toLocaleString('uk-UA')}
                  {' '}за {fmtDate(s.last_run.date_from)}–{fmtDate(s.last_run.date_to)}
                  {s.last_run.started_by_name ? `, ${s.last_run.started_by_name}` : ''}
                </span>
              </>
            ) : (
              <strong>Перевірку ще не виконували — оберіть період і натисніть «перевірити».</strong>
            )}
          </div>

          {shown.map((r) => (
            <RuleBlock key={`${r.code}-${filter}`} rule={r} orgId={effectiveOrg} period={period}
              filter={filter} canManage={canManage} canAi={canAi}
              aiAvailable={aiAvailableText(aiBalanceQ.data)}
              truncated={!!s.last_run?.truncated.includes(r.code)} />
          ))}
          {s.last_run && shown.length === 0 && (
            <p style={{ color: 'var(--muted)' }}>
              {filter === 'OPEN' ? 'Відкритих знахідок за цей період немає.' : 'Знахідок у цьому стані немає.'}
            </p>
          )}

          {s.last_run && clean.length > 0 && (
            <details style={{ marginTop: '1rem' }}>
              <summary style={{ cursor: 'pointer' }}>Перевірено без зауважень: {clean.length}</summary>
              <ul style={{ margin: '0.5rem 0 0', color: 'var(--muted)' }}>
                {clean.map((r) => (
                  <li key={r.code}>{r.title} <span style={{ fontSize: '0.8rem' }}>({r.code})</span></li>
                ))}
              </ul>
            </details>
          )}
        </>
      )}
    </div>
  );
}
